/**
 * Import CSV Modal Component
 *
 * Allows users to select request type and import CSV file
 * Provides download link for CSV template
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './Modal.css';

const ImportCSVModal = ({ onClose, onImport }) => {
  const [selectedRequestType, setSelectedRequestType] = useState('Capability Update Catalog');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const requestTypes = [
    'Capability Update Catalog',
    'Product Master Change',
    'SalesOps Request',
    'Pricing & Packaging',
  ];

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDownloadTemplate = () => {
    // Create a link to download the template file
    const link = document.createElement('a');
    link.href = '/capability-update-catalog-template.csv';
    link.download = 'capability-update-catalog-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alert('Please select a CSV file to import');
      return;
    }

    setUploading(true);
    try {
      await onImport(selectedFile, selectedRequestType);
      onClose();
    } catch (err) {
      console.error('Import error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Import CSV</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="request-type" className="form-label">
              Request Type
            </label>
            <select
              id="request-type"
              className="form-select"
              value={selectedRequestType}
              onChange={(e) => setSelectedRequestType(e.target.value)}
            >
              {requestTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {selectedRequestType === 'Capability Update Catalog' && (
            <div className="template-download">
              <div className="template-info">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="#0066cc">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="template-title">CSV Template Available</p>
                  <p className="template-description">
                    Download the template file to see the required format and column headers
                  </p>
                </div>
              </div>
              <button
                className="template-download-btn"
                onClick={handleDownloadTemplate}
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Download Template
              </button>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="csv-file" className="form-label">
              Select CSV File
            </label>
            <div className="file-input-wrapper">
              <input
                type="file"
                id="csv-file"
                accept=".csv"
                onChange={handleFileChange}
                className="file-input"
              />
              {selectedFile && (
                <p className="file-name">
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {selectedFile.name}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </button>
          <button
            className="modal-button modal-button--primary"
            onClick={handleImport}
            disabled={!selectedFile || uploading}
          >
            {uploading ? (
              <>
                <span className="spinner"></span>
                Importing...
              </>
            ) : (
              'Import CSV'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

ImportCSVModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
};

export default ImportCSVModal;
